import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { RequestPasswordResetPropsInterface } from "../authTypes";
import React, { useMemo, useState } from "react";
import { requestPasswordResetRequest } from "../authApi";
import { Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

export const RequestPasswordReset = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  const schema: yup.Schema<RequestPasswordResetPropsInterface> = useMemo(() => {
    return yup.object({
      email: yup
        .string()
        .email(t("validations.field_must_be_email"))
        .required(t("validations.field_is_required")),
    });
  }, [t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestPasswordResetPropsInterface>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: RequestPasswordResetPropsInterface) => {
    setRateLimitError(null);
    dispatch(requestPasswordResetRequest(data))
      .unwrap()
      .then(() => {
        setEmailSent(true);
      })
      .catch((error: any) => {
        // Obsługa Rate Limit (HTTP 429)
        if (error?.status === 429) {
          const retryAfter = error?.retryAfter;
          let errorMessage = t("rate_limit_exceeded");

          if (retryAfter) {
            // Jeśli Retry-After to liczba sekund
            const seconds = parseInt(retryAfter);
            if (!isNaN(seconds)) {
              const minutes = Math.ceil(seconds / 60);
              errorMessage = t("rate_limit_exceeded_with_time", { minutes });
            }
          }

          setRateLimitError(errorMessage);
          toast.error(errorMessage);
        } else {
          // API zawsze zwraca sukces dla bezpieczeństwa, ale obsłuż ewentualne błędy sieciowe
          setEmailSent(true);
        }
      });
  };

  if (emailSent) {
    return (
      <React.Fragment>
        <div className="d-flex align-content-center flex-wrap justify-content-center vh-100">
          <div className="sign-in-form" style={{ width: "600px" }}>
            <h1 className="mb-0">{t("request_password_reset")}</h1>
            <p className="mt-3">{t("password_reset_email_sent_message")}</p>
            <div className="mt-4">
              <Link to="/login" className="btn btn-primary">
                {t("back_to_login")}
              </Link>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="d-flex align-content-center flex-wrap justify-content-center vh-100">
        <div className="sign-in-form" style={{ width: "600px" }}>
          <h1 className="mb-0">{t("request_password_reset")}</h1>
          <p>{t("request_password_reset_message")}</p>
          {rateLimitError && (
            <div className="alert alert-danger mt-3" role="alert">
              {rateLimitError}
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
            <Row className="mb-4">
              <Form.Group as={Col}>
                <Form.Label>{t("email")}</Form.Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="email"
                      placeholder={t("placeholders.enter_email")}
                      {...field}
                      isInvalid={!!errors.email}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row>
              <Col>
                <div className="d-inline-block w-100">
                  <button type="submit" className="btn btn-primary float-right">
                    {t("send_reset_link")}
                  </button>
                </div>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <Link to="/login">{t("back_to_login")}</Link>
              </Col>
            </Row>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};
